import type * as React from 'react'
import { useMemo } from 'react'
import type { JSX } from 'react'

import { isPast } from 'date-fns'

import { useIsSplitEnabled } from '@buffer-mono/features'

import { PostCard, PostCard_Post } from '~publish/components/PostCard'
import type { PostCard_PostData } from '~publish/components/PostCard'
import { getRecurrenceSummary } from '~publish/components/PostCard/PostCardActions/recurrenceHelpers'
import { PostingTimeline } from '~publish/components/PostingTimeline'
import { maskFragments } from '~publish/graphql'
import type { Service } from '~publish/contexts/AccountContext/graphqlEnums'
import { postSatisfiesPostTypeFilter } from '~publish/helpers/posts/isPostSatisfyingFilters'
import type { FilterablePostType } from '~publish/helpers/postType'
import { useTimezone } from '~publish/hooks/useTimezone'

import { DraggablePostCard } from './DraggablePostCard'
import { OccupiedQueueSlot } from './OccupiedQueueSlot'
import type { SlotOrPost, Slot } from './postSlotManager'
import { QueueSlot } from './QueueSlot'

const isPost = (item: unknown): item is PostCard_PostData => {
  return (
    typeof item === 'object' &&
    item !== null &&
    '__typename' in item &&
    item.__typename === 'Post'
  )
}

const EMPTY_POST_TYPES: readonly FilterablePostType[] = []

/**
 * Utility to split types into isolated types rather than requiring polymorphism,
 * user can now just check if the one they want is null or not.
 * @param item queue slot or post
 * @returns Type split items as tuple
 */
const toSlotAndPost = (
  item: SlotOrPost<PostCard_PostData>,
): [Slot | null, PostCard_PostData | null] => {
  if (isPost(item)) {
    return [null, item]
  }
  return [item, null]
}

interface SlotOrPostCardProps extends React.HTMLAttributes<HTMLDivElement> {
  item: SlotOrPost<PostCard_PostData>
  tagIds: string[]
  postTypes?: readonly FilterablePostType[]
  isDragAndDropEnabled: boolean
  channelId: string
  channelService?: Service
  organizationId?: string
}

export const SlotOrPostCard = (props: SlotOrPostCardProps): JSX.Element => {
  const {
    item,
    tagIds,
    postTypes = EMPTY_POST_TYPES,
    channelId,
    channelService,
    organizationId,
    isDragAndDropEnabled,
    ...restProps
  } = props
  const [slot, post] = toSlotAndPost(item)
  const isRecurringEnabled = useIsSplitEnabled('publish-recurring-posts')
  const timeZone = useTimezone()
  const recurrenceSummary =
    post && post.status === 'scheduled'
      ? getRecurrenceSummary(
          post.recurrence,
          post.dueAt,
          isRecurringEnabled,
          timeZone,
        )
      : null

  // The queue fetches every post so it can tell which slots are taken, so a
  // filtered-out post is hidden here rather than excluded from the query. It
  // still renders as an occupied slot below, which keeps the schedule honest.
  const isHiddenPost = useMemo(() => {
    if (!post) return false
    const failsTagFilter =
      tagIds.length > 0 && !post.tags.some((tag) => tagIds.includes(tag.id))
    const failsPostTypeFilter = !postSatisfiesPostTypeFilter(
      post.metadata?.type,
      postTypes,
    )
    return failsTagFilter || failsPostTypeFilter
  }, [post, tagIds, postTypes])

  // Available Slot
  if (slot) {
    return (
      <PostingTimeline.Entry data-testid="queue-entry">
        <PostingTimeline.TimeLabel date={slot.date} />
        <PostingTimeline.Content>
          <QueueSlot
            date={slot.date}
            channelId={channelId}
            channelService={channelService}
            organizationId={organizationId}
            tags={tagIds}
            {...restProps}
          />
        </PostingTimeline.Content>
      </PostingTimeline.Entry>
    )
  }

  if (!post || !post.dueAt) {
    // this can't happen, but type checker doesn't enforce it
    // additionally, dueAt will never be null for a post in the queue
    return <></>
  }

  // Occupied slot
  if (isHiddenPost) {
    return (
      <PostingTimeline.Entry data-testid="queue-entry">
        <PostingTimeline.TimeLabel
          date={post.dueAt}
          status={post.status}
          schedulingType={post.schedulingType}
          disabled
          recurring={!!recurrenceSummary}
          recurrenceSummary={recurrenceSummary}
        />
        <PostingTimeline.Content>
          <OccupiedQueueSlot date={post.dueAt} />
        </PostingTimeline.Content>
      </PostingTimeline.Entry>
    )
  }

  // DND supporting post
  if (isDragAndDropEnabled && post.allowedActions.includes('updatePost')) {
    return (
      <PostingTimeline.Entry data-testid="queue-entry">
        <PostingTimeline.TimeLabel
          date={post.dueAt}
          status={post.status}
          schedulingType={post.schedulingType}
          customScheduled={post.isCustomScheduled}
          isPinned={post.isPinned}
          overdue={isPast(post.dueAt) && post.status !== 'sent'}
          recurring={!!recurrenceSummary}
          recurrenceSummary={recurrenceSummary}
        />
        <PostingTimeline.Content>
          <DraggablePostCard post={maskFragments([PostCard_Post], post)} />
        </PostingTimeline.Content>
      </PostingTimeline.Entry>
    )
  }

  // Regular post
  return (
    <PostingTimeline.Entry data-testid="queue-entry">
      <PostingTimeline.TimeLabel
        date={post.dueAt}
        status={post.status}
        schedulingType={post.schedulingType}
        customScheduled={post.isCustomScheduled}
        isPinned={post.isPinned}
        includeDate={post.error !== null}
        overdue={
          isPast(post.dueAt) && post.status !== 'sent' && post.error === null
        }
        recurring={!!recurrenceSummary}
        recurrenceSummary={recurrenceSummary}
      />
      <PostingTimeline.Content>
        <PostCard post={maskFragments([PostCard_Post], post)} rounded="lg" />
      </PostingTimeline.Content>
    </PostingTimeline.Entry>
  )
}
